import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinearProgress } from '@material-ui/core';
import { chain } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import VoxelIconButton from '../../../../components/voxel/Buttons/VoxelIconButton';
import { TimelineChartProps } from '../../../../components/voxel/Charts/TimelineChart';
import VoxelText from '../../../../components/voxel/Text/VoxelText';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';
import { KeywordFilteringGq } from '../../../../graphql/projects/models/KeywordFilteringGq';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { QueuedItemStatus } from '../../../../graphql/projects/models/QueuedItemStatus';
import { emptySerpProgressAnalysis } from '../../../../graphql/projects/models/SerpProgressAnalysisGq';
import { TagGroupGq } from '../../../../graphql/projects/models/TagGroupGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import ProgressDashboardMiniature from '../ProgressDashboardMiniature';
import ProgressDashboardMiniatureChartContainer from '../ProgressDashboardMiniatureChartContainer';
import ProgressDashboardMiniatureTitle from '../ProgressDashboardMiniatureTitle';
import ProgressDashboardTagsChartRank from './ProgressDashboardTagsChartAverageRank';
import ProgressDashboardTagsChartTraffic from './ProgressDashboardTagsChartTraffic';
import ProgressDashboardTagsChartWeightedRank from './ProgressDashboardTagsChartWeightedRank';
import { DataPoint, TagData, tagColors } from './models';

export interface ProgressDashboardTagsProps {
  project: ProjectGq;
  filter: KeywordFilteringGq;
  pollInterval?: number;
  width: number;
  tagGroupId: number;
  tagGroup: TagGroupGq;
  tagIds: number[];
  setDashboardLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export type SiteProgressCharts = 'weighted-rank' | 'traffic' | 'rank';

export interface ForwardedChartProps
  extends Omit<
    TimelineChartProps,
    'dataSeries' | 'yMaxPlaceholder' | 'dataColors' | 'yLabelTooltip'
  > {}

export const ProgressDashboardTags: React.FC<ProgressDashboardTagsProps> = ({
  project,
  filter,
  pollInterval,
  width,
  tagGroupId,
  tagIds,
  tagGroup,
  setDashboardLoading,
}) => {
  const domainId = project.domains.find((d) => d.type === DomainTypeGq.PROJECT)?.id;
  const skip = !filter?.progressRequestIds?.length || !tagGroupId || !domainId;
  const options = skip ? { skip: true } : { pollInterval: pollInterval };

  const progressAnalysisQuery =
    projectsApiService.queries.progress.useGetSerpProgressTagGroupAnalysis(
      {
        projectId: project.id,
        tagGroupId: tagGroupId,
        domainId: project.domains.find((d) => d.type === DomainTypeGq.PROJECT)?.id,
        keywordQuery: {
          filtering: filter,
        },
      },
      options
    );
  const loading = progressAnalysisQuery.query.loading && !progressAnalysisQuery.query.data;
  useEffect(() => setDashboardLoading(loading), [loading]);
  const progressAnalysisData = progressAnalysisQuery?.result;
  const [selectedChart, setSelectedChart] = useState<SiteProgressCharts>();

  // Charts
  const smallFontSize = 11;
  const paddingWidth = 3.325 * 4;
  const boxWidth = (width - 2) / 3;
  const smallChartProps: ForwardedChartProps = {
    paddingTop: smallFontSize / 2,
    width: boxWidth - paddingWidth * 2 - 280,
    paddingBottom: smallFontSize / 2,
    paddingLeft: smallFontSize * 3,
    paddingRight: 0,
    fontSizeAxes: smallFontSize,
    showXGrid: true,
    showYGrid: true,
    showXLabels: false,
    showYLabels: true,
    height: 32 * 3.5,
    yTickCount: 2,
    strokeWidth: 1.5,
    loadingPlaceholder: loading,
  };

  const smallChartMinHeightClass = 'min-h-[120px]';

  const largeFontSize = 13;
  const bigChartProps: ForwardedChartProps = {
    paddingTop: largeFontSize * 1,
    width: width - paddingWidth * 2,
    paddingBottom: largeFontSize * 2,
    paddingLeft: largeFontSize * 5,
    paddingRight: largeFontSize * 4,
    fontSizeAxes: largeFontSize,
    fontSizeTooltip: 14,
    showXGrid: true,
    showYGrid: true,
    yTickCount: 5,
    height: 100 * 3.5,
    strokeWidth: 3,
    enableTooltips: true,
    loadingPlaceholder: loading,
  };
  const bigChartMinHeightClass = 'min-h-[374px]';

  const createOnClickChart = (chart: SiteProgressCharts) => () =>
    setSelectedChart((prev) => (prev === chart ? undefined : chart));

  const tagSeries = useMemo(() => {
    const serpTrackingRequests = project.projectSerpTrackingRequests.filter(
      (sr) => sr.status === QueuedItemStatus.SUCCEEDED
    );
    return tagIds
      ?.map(
        (tagId, index): TagData => ({
          tag: tagGroup.tags.find((tg) => tg.id === tagId),
          color: tagColors[index % tagColors.length],
          data:
            chain(serpTrackingRequests)
              .flatMap(
                (str): DataPoint => ({
                  ...emptySerpProgressAnalysis,
                  scheduledTime: str.scheduledTime,
                  serpRequestId: str.id,
                  ...progressAnalysisData.find(
                    (pad) => pad.tagId === tagId && pad.serpRequestId === str.id
                  ),
                })
              )
              .orderBy((d) => d.scheduledTime)
              .filter((d) => d.keywordCount > 0)
              .value() ?? [],
        })
      )
      .filter((tg) => !!tg.tag && !!tg.data?.length);
  }, [project.domains, progressAnalysisData, tagIds, tagGroup]);

  return (
    <div>
      <div className="grid grid-cols-3 gap-4">
        {/* Traffic mini */}
        <ProgressDashboardMiniature
          selected={selectedChart === 'traffic'}
          white={!selectedChart}
          onClick={createOnClickChart('traffic')}
          className=""
        >
          <ProgressDashboardMiniatureTitle
            title="Traffic"
            tooltip="Traffic estimation calculated as Volume x CTR. CTR is based on rank and comes from a statistical study."
            loadingPlaceholder={loading}
          />
          <div className="flex-grow" />
          <ProgressDashboardMiniatureChartContainer minHeightClass={smallChartMinHeightClass}>
            <ProgressDashboardTagsChartTraffic
              tagDataSeries={tagSeries}
              legend
              timelineChartProps={{
                ...smallChartProps,
              }}
            />
          </ProgressDashboardMiniatureChartContainer>
        </ProgressDashboardMiniature>

        {/* Rank mini */}
        <ProgressDashboardMiniature
          className={`rounded-tl-lg ${!selectedChart && 'rounded-bl-lg'}`}
          selected={selectedChart === 'rank'}
          white={!selectedChart}
          onClick={createOnClickChart('rank')}
        >
          <ProgressDashboardMiniatureTitle
            title="Rank"
            tooltip="Calculated average rank."
            loadingPlaceholder={loading}
          />
          <div className="flex-grow" />
          <ProgressDashboardMiniatureChartContainer minHeightClass={smallChartMinHeightClass}>
            <ProgressDashboardTagsChartRank
              tagDataSeries={tagSeries}
              legend
              timelineChartProps={{
                ...smallChartProps,
              }}
            />
          </ProgressDashboardMiniatureChartContainer>
        </ProgressDashboardMiniature>

        {/* Weighted rank mini */}
        <ProgressDashboardMiniature
          className={`rounded-tl-lg ${!selectedChart && 'rounded-bl-lg'}`}
          selected={selectedChart === 'weighted-rank'}
          white={!selectedChart}
          onClick={createOnClickChart('weighted-rank')}
        >
          <ProgressDashboardMiniatureTitle
            title="Weighted rank"
            loadingPlaceholder={loading}
            tooltip="Calculated average rank, weighted by volume. This makes high volume keywords more impactful."
          />
          <div className="flex-grow" />
          <ProgressDashboardMiniatureChartContainer minHeightClass={smallChartMinHeightClass}>
            <ProgressDashboardTagsChartWeightedRank
              tagDataSeries={tagSeries}
              legend
              timelineChartProps={{
                ...smallChartProps,
              }}
            />
          </ProgressDashboardMiniatureChartContainer>
        </ProgressDashboardMiniature>
      </div>

      {/* Big charts */}
      {!!selectedChart && (
        <div className="px-6 pt-4 bg-white rounded-xl shadow-sm mt-4">
          {/* Text section */}
          <div className="flex flex-row items-center pb-3">
            <VoxelText variant="page subtitle" paddingBot className="flex-grow">
              {selectedChart === 'rank' && 'Rank'}
              {selectedChart === 'weighted-rank' && 'Weighted rank'}
              {selectedChart === 'traffic' && 'Traffic'}
            </VoxelText>
            <VoxelIconButton
              icon={<FontAwesomeIcon icon={faTimes} size="lg" />}
              color="white"
              onClick={() => setSelectedChart(undefined)}
            />
          </div>

          {/* Chart */}
          <div className={`pb-6 ${bigChartMinHeightClass}`}>
            {selectedChart === 'rank' && (
              <ProgressDashboardTagsChartRank
                tagDataSeries={tagSeries}
                timelineChartProps={{
                  ...bigChartProps,
                }}
              />
            )}
            {selectedChart === 'traffic' && (
              <ProgressDashboardTagsChartTraffic
                tagDataSeries={tagSeries}
                timelineChartProps={{
                  ...bigChartProps,
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressDashboardTags;
